import { useEffect } from 'react';
import useMedia from '../../../hooks/useMedia';
import MediaSection from './MediaSection';
import { GoogleSearchPreview } from 'components';
import { GOOGLE_MAX_FILE_SIZE, MB } from 'constants/utils';


const GooglePreview = ({ campaignPreview, isCampaignSent, onValidationChange, onChange, onRefetchCampaign }) => {
  const {
    loadingLogo,
    uploadingMedia,
    mediaFiles,
    logo,
    logoInputRef,
    mediaInputRef,
    dragActive,
    mediaErrors,
    getAllMedia,
    handleDrag,
    handleDrop,
    handleInputChange,
    handleLogoInputChange,
    removeLogo,
    removeMedia,
    handleMediaError,
    setMediaErrors,
  } = useMedia(campaignPreview.id, 'google', onRefetchCampaign);

  useEffect(() => {
    if (campaignPreview.id) {
      getAllMedia();
    }
  }, [campaignPreview.id, getAllMedia]);

  useEffect(() => {
    if (onValidationChange) {
      const isPerformanceMax = campaignPreview?.campaign_type === 'PERFORMANCE_MAX';
      const hasLogo = Boolean(logo);
      const hasMedia = mediaFiles.length > 0;
      
      // For Performance Max, both logo and media are required
      const isValid = !isPerformanceMax || (hasLogo && hasMedia);
      onValidationChange(isValid);
    }
  }, [logo, mediaFiles, campaignPreview.campaign_type, onValidationChange]);

  const getCampaignDescription = () => {
    if (campaignPreview.ad_content?.descriptions?.length > 0) {
      return campaignPreview.ad_content.descriptions[0];
    }
    return `Descripción del anuncio`;
  };

  const getCampaignTitle = () => {
    return campaignPreview.ad_content?.headlines?.[0] || `Título del anuncio`;
  };

  const getCampaignURL = () => {
    return campaignPreview.ad_content?.final_url || `https://www.ejemplo.com/landing`;
  };

  const getCampaignKeywords = () => {
    if (campaignPreview.google_settings?.keywords?.length > 0) {
      return campaignPreview.google_settings.keywords.join(', ');
    }
    return `Keywords del anuncio`;
  };

  // Funciones para obtener arrays completos de títulos y descripciones
  const getAllTitles = () => {
    return campaignPreview.ad_content?.headlines || [`Título del anuncio`];
  };

  const getAllDescriptions = () => {
    return campaignPreview.ad_content?.descriptions || [`Descripción del anuncio`];
  };

  const isPerformanceMax = campaignPreview.campaign_type === 'PERFORMANCE_MAX';

  return (
    <div className="space-y-4">
      {/* Anuncio de texto de Google */}
      <GoogleSearchPreview
        title={getCampaignTitle()}
        url={getCampaignURL()}
        description={getCampaignDescription()}
        keywords={getCampaignKeywords()}
        titles={getAllTitles()}
        descriptions={getAllDescriptions()}
        hideKeywords={isPerformanceMax}
        compact={true}
        onChange={onChange}
      />

      {/* Área de medios - Solo mostrar para Performance Max */}
      {isPerformanceMax && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Logo section */}
          <MediaSection
            mediaFiles={logo ? [logo] : []}
            loading={loadingLogo}
            mediaErrors={mediaErrors}
            fileInputRef={logoInputRef}
            dragActive={dragActive}
            handleInputChange={handleLogoInputChange}
            handleDrag={handleDrag}
            handleDrop={handleDrop}
            removeMedia={removeLogo}
            handleMediaError={handleMediaError}
            setMediaErrors={setMediaErrors}
            title="Logo de la marca"
            uploadText={`Subí el logo de tu marca (máx. ${GOOGLE_MAX_FILE_SIZE / MB} MB)`}
            uploadDescription="Logo cuadrado y de alta calidad. Obligatorio para campañas Performance Max."
            disabled={isCampaignSent}
            type="logo"
            campaignId={campaignPreview.id}
            required={true}
            compact={true}
            onRefetchCampaign={onRefetchCampaign}
          />

          {/* Media section */}
          <MediaSection
            mediaFiles={mediaFiles.filter(media => media.type === 'generic')}
            loading={uploadingMedia}
            mediaErrors={mediaErrors}
            fileInputRef={mediaInputRef}
            dragActive={dragActive}
            handleInputChange={handleInputChange}
            handleDrag={handleDrag}
            handleDrop={handleDrop}
            removeMedia={removeMedia}
            handleMediaError={handleMediaError}
            setMediaErrors={setMediaErrors}
            title="Imágenes del anuncio"
            uploadText={`Subí imágenes para tu anuncio (máx. ${GOOGLE_MAX_FILE_SIZE / MB} MB)`}
            uploadDescription="Las imágenes mejoran el rendimiento. Obligatorio en Performance Max."
            disabled={isCampaignSent}
            type="generic"
            campaignId={campaignPreview.id}
            required={true}
            acceptedFiles="image/*"
            compact={true}
            onRefetchCampaign={onRefetchCampaign}
          />
        </div>
      )}
    </div>
  );
};

export default GooglePreview;